import { StatsigClient } from '@statsig/js-client';
import { toRaw } from 'vue';
import { Event, StatsigTracking, StatsigTrackingExtra } from './types';
import { configBase } from '@/config/base';
import { isUsingFakeData } from '@/components/shared/DevPanel/DevPanel.helpers';
import store from '@/store';
import { Market } from '@shared/config/market/market.models';
import { BREAKPOINTS } from '@shared/consts/breakpoints';
import { getAffiliateCookieData } from '@/helpers/cookie/affiliateCookie';
import { BookingSalesChannel, CartAffiliate } from '@/api/Cart/cart.interfaces';
import { migrateOldSdkStableIdToNewSdk } from '@/tracking/statsig/utils';

export async function setupStatsig() {
    const statsigClient = new StatsigClient(
        configBase.tracking.statsig.key,
        {},
        {
            environment: {
                tier: configBase.tracking.statsig.environment,
            },
        }
    );

    migrateOldSdkStableIdToNewSdk();
    await statsigClient.initializeAsync();
}

export const pushStatsig = (e: Event) => {
    e.metadata = {
        ...getRecommendedMetadata(),
        ...getAdditionalMetadata(),
        ...e.metadata,
    };

    if (!configBase.tracking.enabled || isUsingFakeData() || configBase.isTest) {
        pushStatsigDebug(e);

        return;
    }

    window.statsig = window.statsig || [];
    window.statsig.push({
        eventName: e.eventName,
        metadata: e.metadata,
    });

    StatsigClient.instance(configBase.tracking.statsig.key).logEvent(
        e.eventName,
        undefined,
        e.metadata
    );
};

function pushStatsigDebug(e: Event) {
    window.statsigDebug = window.statsigDebug || [];
    window.statsigDebug.push({
        eventName: e.eventName,
        metadata: e.metadata,
    });
}

function getPage() {
    return store.getters['general/getPage'];
}

function getPrevPage() {
    return store.getters['general/getPrevPage'];
}

function getShoppingCartKey() {
    return store.getters['cart/getShoppingCartKey'];
}

function getAffiliateData(): CartAffiliate | null {
    return store.getters['cart/getAffiliate'];
}

function getBookingToken(): string | null {
    return store.getters['cart/getBookingToken'];
}

function getBookingSalesChannels(): BookingSalesChannel[] {
    return toRaw(store.getters['cart/getBookingSalesChannels']);
}

function getAdditionalMetadata(): StatsigTrackingExtra {
    const affiliateCookie = getAffiliateCookieData();
    const affiliate = getAffiliateData();
    const hasBookingToken = Boolean(getBookingToken());

    return {
        referrer: document.referrer,
        isBooking: hasBookingToken,
        page: getPage(),
        prevPage: getPrevPage(),
        shoppingCartKey: getShoppingCartKey(),
        bookingSalesChannels: getBookingSalesChannels(),
        mainSalesChannel: affiliate?.SalesChannel,
        businessArea: affiliate?.BusinessArea,
        affiliateGroupName: affiliate?.GroupName,
        affiliateId:
            affiliate?.AffiliateId ?? affiliateCookie?.affiliateCookieParams?.AffiliateId,
    };
}

function getRecommendedMetadata(): StatsigTracking {
    const market: Market = store.getters['general/getMarket'];
    const isDesktop = document.documentElement.clientWidth >= BREAKPOINTS.lg;

    return {
        hostname: market.domain,
        language: market.locale,
        href: window.location.href,
        isDesktop,
    };
}
